import {
  Box,
  Card,
  CardContent,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { ClipboardList, Languages, Plus } from "lucide-react";
import { useState } from "react";
import professionalProfileApi from "../../../api/professionalProfile";
import Button from "../../../components/common/Button";
import CustomLoader from "../../../components/common/CustomLoader";
import EmptyStateCard from "../../../components/professional/EmptyStateCard";
import Modal from "../../../components/professional/Modal";
import IcaoProficiencyForm from "./IcaoProficiencyForm";

const IcaoLanguageProficiencyTab = ({
  icao,
  onModification,
  setNotification,
  dropDownOptions
}) => {
  const [errors, setErrors] = useState({});
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [selectedIcao, setSelectedIcao] = useState(null);

  const handleOpen = (icao = null) => {
    setSelectedIcao(icao);
    setOpen(true);
  };

  const handleClose = () => {
    setSelectedIcao(null);
    setOpen(false);
    setErrors({});
  };

  const handleSave = async (data) => {
    setLoading(true);
    try {
      let payload = { ...data };

      if (selectedIcao && payload.id) {
        // Update existing
        const res = await professionalProfileApi.updateSingleResource(
          data.id,
          "icao-language-proficiencies",
          payload
        );
        if (res.status === 200) {
          onModification();
          setNotification(
            "Icao language proficiency updated successfully",
            "success"
          );
        }
      } else {
        // Add new
        delete payload.id;
        const res = await professionalProfileApi.updateProfessionalProfile({
          icao_language_proficiencies: [payload],
        });
        if (res.status === 200 || res.status === 201) {
          onModification();
          setNotification(
            "Icao language proficiency added successfully",
            "success"
          );
        }
      }
      handleClose();
    } catch (error) {
      console.error(error);
      if (error?.response?.data?.error)
        return setErrors(error?.response?.data?.error[0]);
      setNotification(
        "An error occurred while updating icao language proficiency",
        "error"
      );
      handleClose();
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <CustomLoader />;

  const dialogTitle = selectedIcao
    ? "Edit Icao Proficiency"
    : "Add Icao Proficiency";

  return (
    <div>
      {/* Empty State */}
      {icao?.length === 0 ? (
        <EmptyStateCard
          icon={<Languages size={48} color="#1976d2" />}
          title="No icao language proficiency added yet"
          description="Add your Icao language proficiency"
          buttonText="Add proficiency"
          handleOpen={handleOpen}
        />
      ) : (
        <>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "stretch", sm: "center" },
              gap: 2,
              mb: 3,
            }}
          >
            <Typography variant="h6">Icao Language Proficiencies</Typography>
            <Button
              startIcon={<Plus size={18} />}
              variant="contained"
              color="primary"
              onClick={() => handleOpen()}
            >
              Add proficiency
            </Button>
          </Box>

          {/* Body Cards */}
          <Grid container spacing={3}>
            {icao?.map((ic) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={ic.id}>
                <Card
                  sx={{
                    borderRadius: 3,
                    boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                    transition: "0.2s",
                    "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.15)" },
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <CardContent>
                    {/* Language */}
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <ClipboardList size={16} style={{ marginRight: 8 }} />
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600 }}
                        color="text.primary"
                      >
                        Language: {ic?.language}
                      </Typography>
                    </Box>

                    {/* Country */}
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      Icao Level: {ic?.icao_level}
                    </Typography>

                    {/* Issuing Authority */}

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      Issuing Authority: {ic?.issuing_authority}
                    </Typography>

                    {/* Assessment date */}
                    <Typography variant="body2" color="text.secondary">
                      Assessment date: {ic?.assessment_date}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Certificate Expiry date: {ic?.certificate_expiry_date}
                    </Typography>
                  </CardContent>

                  {/* Actions */}
                  <CardActions sx={{ justifyContent: "flex-end" }}>
                    <IconButton color="primary" onClick={() => handleOpen(ic)}>
                      <Pencil size={18} />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* Add/Edit Dialog */}
      <Modal handleClose={handleClose} open={open} title={dialogTitle}>
        <IcaoProficiencyForm
          errors={errors}
          icao={selectedIcao}
          onCancel={handleClose}
          onSave={handleSave}
          setErrors={setErrors}
          dropDownOptions={dropDownOptions}
        />
      </Modal>
    </div>
  );
};

export default IcaoLanguageProficiencyTab;
